import { useAuth0 } from '@auth0/auth0-react';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Organization, ReduxState } from 'types';
import { getOrganizationSuspendedMessage } from 'utils/models/Organization';
import { logOutAndCleanUp } from 'utils/models/User';
import MarkdownViewer from 'views/Widgets/Inputs/MarkdownViewer';
import SimplePage from '../SimplePage';

interface Props {
  currentOrganization?: Organization;
  // message surfaced by the API when it locks the user out; when provided it
  // takes precedence over the organization in the store, which may not have
  // loaded at all
  suspendedMessage?: string | null;
}

const PageSuspendedOrganization: FC<Props> = ({
  currentOrganization,
  suspendedMessage: suspendedMessageFromApi,
}) => {
  const { formatMessage } = useIntl();

  const { logout } = useAuth0();

  const suspendedMessage =
    suspendedMessageFromApi !== undefined
      ? suspendedMessageFromApi
      : currentOrganization
        ? getOrganizationSuspendedMessage(currentOrganization)
        : null;

  return (
    <SimplePage
      title={formatMessage({
        id: 'app.views.layout.pages.errors.page_suspended_organization.title.your_organization_is_on_hold',
        defaultMessage: 'Your organization is on hold',
      })}
    >
      {suspendedMessage ? (
        <MarkdownViewer content={suspendedMessage} />
      ) : (
        <p>
          <FormattedMessage
            id="app.views.layout.pages.errors.page_suspended_organization.on_hold"
            defaultMessage="
          Access to Confirm is temporarily unavailable for your organization.
          Please contact your internal team to find out more information."
          />
        </p>
      )}
      <p>
        <FormattedMessage
          id="app.views.layout.pages.errors.page_suspended_organization.log_out"
          defaultMessage="If you meant to log in with a different email address, <link>log out</link> and log back in with the correct email address."
          values={{
            link: (chunks) => (
              <span
                className="btn-link"
                role="button"
                onClick={() => logOutAndCleanUp(logout)}
              >
                {chunks}
              </span>
            ),
          }}
        />
      </p>
    </SimplePage>
  );
};

const mapStateToProps = (state: ReduxState) => {
  const { currentOrganization } = state;

  return {
    currentOrganization,
  };
};

// all tracking in app will be passed through here
export default connect(mapStateToProps)(React.memo(PageSuspendedOrganization));
